import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Descriptions, Drawer, Input, message, Row, Select, Space,
  Statistic, Table, Tag, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import request from '@/utils/request';
import { CosImage } from '@/components/CosImage';
import type {
  ImageGenLogItem,
  ImageGenLogListResponse,
  ImageGenSummaryResponse,
} from '@zero/shared';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

type HydratedImage = { id: number; cosKey: string | null };
interface ImageGenLogDetail extends ImageGenLogItem {
  inputPayload: unknown;
  errorUpstream: unknown;
  sourceImages: HydratedImage[];
  outputImages: HydratedImage[];
}

const statusColor: Record<string, string> = {
  success: 'green',
  failed: 'red',
  timeout: 'orange',
};

const ACTOR_OPTIONS = [
  { value: 'user', label: 'user' },
  { value: 'admin', label: 'admin' },
  { value: 'system', label: 'system' },
];

const STATUS_OPTIONS = [
  { value: 'success', label: 'success' },
  { value: 'failed', label: 'failed' },
  { value: 'timeout', label: 'timeout' },
];

function formatCost(v: string | null): string {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n < 0.0001) return `$${n.toExponential(2)}`;
  return `$${n.toFixed(6)}`;
}

const ImageGenLogPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ImageGenLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [modelSlug, setModelSlug] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [actorType, setActorType] = useState<string>();
  const [errorCode, setErrorCode] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const [summary, setSummary] = useState<ImageGenSummaryResponse | null>(null);

  const [detail, setDetail] = useState<ImageGenLogDetail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize };
      if (modelSlug) params.modelSlug = modelSlug;
      if (status) params.status = status;
      if (actorType) params.actorType = actorType;
      if (errorCode) params.errorCode = errorCode;
      if (range) {
        params.startAt = range[0].startOf('day').toISOString();
        params.endAt = range[1].endOf('day').toISOString();
      }
      const res = await request.get<ImageGenLogListResponse>('/admin/image-gen/calls', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      message.error((err as Error).message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, modelSlug, status, actorType, errorCode, range]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await request.get<ImageGenSummaryResponse>('/admin/image-gen/calls/summary');
      setSummary(res.data);
    } catch (err) {
      // non-critical
      console.error(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const openDetail = async (id: number) => {
    try {
      const res = await request.get<ImageGenLogDetail>(`/admin/image-gen/calls/${id}`);
      setDetail(res.data);
    } catch (err) {
      message.error((err as Error).message || '加载详情失败');
    }
  };

  const columns: ColumnsType<ImageGenLogItem> = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '来源',
      render: (_, r) => (
        <Tag>
          {r.actorType}
          {r.actorId != null ? `#${r.actorId}` : ''}
        </Tag>
      ),
      width: 110,
    },
    {
      title: '模型',
      dataIndex: 'modelSlug',
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '费用',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{formatCost(r.costUsd)}</Text>
          {r.priceMatchKey && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.priceMatchKey}
            </Text>
          )}
        </Space>
      ),
      width: 120,
    },
    {
      title: '源图',
      width: 80,
      render: (_, r) =>
        r.cacheHit == null ? (
          '—'
        ) : (
          <Tag color={r.cacheHit ? 'green' : 'orange'}>
            {r.cacheHit ? 'HIT' : 'MISS'}
          </Tag>
        ),
    },
    {
      title: '耗时',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>总 {r.latencyMs ?? '—'}ms</Text>
          {r.uploadLatencyMs != null && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              上传 {r.uploadLatencyMs}ms
            </Text>
          )}
        </Space>
      ),
      width: 120,
    },
    {
      title: '错误',
      dataIndex: 'errorCode',
      render: (v) => v ? <Tag color="red">{v}</Tag> : '—',
      width: 140,
    },
    {
      title: '操作',
      render: (_, r) => (
        <Button size="small" onClick={() => openDetail(r.id)}>
          详情
        </Button>
      ),
      width: 80,
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginTop: 0 }}>图像生成日志</Title>

      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card><Statistic title="今日调用" value={summary.today.callCount} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="今日费用" value={formatCost(summary.today.totalCostUsd)} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="本月调用" value={summary.month.callCount} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="本月费用" value={formatCost(summary.month.totalCostUsd)} /></Card>
          </Col>
        </Row>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="modelSlug"
            allowClear
            value={modelSlug}
            onChange={(e) => setModelSlug(e.target.value || undefined)}
            style={{ width: 180 }}
          />
          <Select
            placeholder="status"
            allowClear
            style={{ width: 130 }}
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
          />
          <Select
            placeholder="actorType"
            allowClear
            style={{ width: 130 }}
            options={ACTOR_OPTIONS}
            value={actorType}
            onChange={setActorType}
          />
          <Input
            placeholder="errorCode"
            allowClear
            value={errorCode}
            onChange={(e) => setErrorCode(e.target.value || undefined)}
            style={{ width: 180 }}
          />
          <RangePicker
            value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => { setPage(1); load(); loadSummary(); }}>
            刷新
          </Button>
        </Space>
      </Card>

      {summary && summary.byModel.length > 0 && (
        <Card title="按模型汇总" style={{ marginBottom: 16 }}>
          <Table
            rowKey="key"
            size="small"
            columns={[
              { title: '模型', dataIndex: 'key' },
              { title: '调用', dataIndex: 'callCount' },
              { title: '成功', dataIndex: 'successCount' },
              { title: '失败/超时', dataIndex: 'failedCount' },
              {
                title: '费用',
                dataIndex: 'totalCostUsd',
                render: (v) => formatCost(v),
              },
            ]}
            dataSource={summary.byModel}
            pagination={false}
          />
        </Card>
      )}

      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => setPage(p),
            showTotal: (t) => `共 ${t} 条`,
          }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Drawer
        title={detail ? `调用详情 #${detail.id}` : ''}
        open={!!detail}
        onClose={() => setDetail(null)}
        width={720}
      >
        {detail && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="时间" span={2}>
                {dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Actor">
                {detail.actorType}{detail.actorId != null ? `#${detail.actorId}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor[detail.status] || 'default'}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Model" span={2}>
                <Text code>{detail.modelSlug}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>{detail.endpoint}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Request ID">
                {detail.requestId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="耗时">
                {detail.latencyMs ?? '—'} ms
                {detail.uploadLatencyMs != null && (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    (上传 {detail.uploadLatencyMs}ms)
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="费用">
                {formatCost(detail.costUsd)}
                {detail.priceMatchKey && <Tag style={{ marginLeft: 8 }}>{detail.priceMatchKey}</Tag>}
                {detail.costSource && <Tag>{detail.costSource}</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="缓存">
                {detail.cacheHit == null ? '—' : detail.cacheHit ? 'HIT' : 'MISS'}
              </Descriptions.Item>
              {detail.errorCode && (
                <Descriptions.Item label="错误" span={2}>
                  <Tag color="red">{detail.errorCode}</Tag>
                  <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
                    {detail.errorMessage}
                  </Paragraph>
                </Descriptions.Item>
              )}
            </Descriptions>

            {detail.sourceImages.length > 0 && (
              <Card size="small" title="源图">
                <Space wrap>
                  {detail.sourceImages.map((img) => (
                    <CosImage
                      key={img.id}
                      cosKey={img.cosKey ?? undefined}
                      maxWidth={180}
                      maxHeight={180}
                    />
                  ))}
                </Space>
              </Card>
            )}

            {detail.outputImages.length > 0 && (
              <Card size="small" title="生成结果">
                <Space wrap>
                  {detail.outputImages.map((img) => (
                    <CosImage
                      key={img.id}
                      cosKey={img.cosKey ?? undefined}
                      maxWidth={240}
                      maxHeight={240}
                    />
                  ))}
                </Space>
              </Card>
            )}

            <Card size="small" title="Input 摘要">
              <pre style={{ fontSize: 12, margin: 0 }}>
                {JSON.stringify(detail.inputSummary, null, 2)}
              </pre>
            </Card>

            <Card size="small" title="Input Payload (脱敏)">
              <pre style={{ fontSize: 12, margin: 0, overflow: 'auto', maxHeight: 400 }}>
                {JSON.stringify(detail.inputPayload, null, 2)}
              </pre>
            </Card>

            {detail.errorUpstream != null && (
              <Card size="small" title="Upstream 错误原文">
                <pre style={{ fontSize: 12, margin: 0, overflow: 'auto', maxHeight: 400 }}>
                  {JSON.stringify(detail.errorUpstream, null, 2)}
                </pre>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default ImageGenLogPage;
